<template>
  <!--
    PoolaFestivalOpening — the one-time "🎉 موسم البولة فتح دلوقتي" reveal shown the
    moment the run flips SCHEDULED→ACTIVE (festival P2-B). A SINGLE confetti burst
    + a short haptic fire here — the COMMUNITY-OPEN moment.

    HARD RULE: confetti/haptic celebrate the community open (and collective
    milestones) ONLY — NEVER a member's own claim. This component is mounted by
    PoolaRun exclusively on the observed status flip, guarded once-per-run, so it
    can never be triggered by a purchase.

    The parent controls the "fire once" lifecycle (it mounts this only on the
    genuine flip and drops it after `done`); this component just plays the reveal
    and self-dismisses. reduced-motion → NO confetti/haptic, only an opacity fade.
  -->
  <div class="poola-opening" :data-reduced="reduced" role="status" aria-live="polite">
    <div class="poola-opening__banner">
      <span class="poola-opening__text">{{ t('poola.festival.opening.live') }}</span>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { fireConfetti, prefersReducedMotion } from '@/utils/confetti.js';

const emit = defineEmits(['done']);

const AUTO_DISMISS_MS = 4800;

const { t } = useI18n({ useScope: 'global' });

const reduced = ref(prefersReducedMotion());
let dismissTimer = null;

onMounted(() => {
  if (!reduced.value) {
    // SINGLE burst — the community-open celebration.
    fireConfetti();
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try {
        navigator.vibrate([12, 40, 12]);
      } catch {
        // haptic is best-effort; ignore unsupported/blocked
      }
    }
  }
  dismissTimer = window.setTimeout(() => emit('done'), AUTO_DISMISS_MS);
});

onBeforeUnmount(() => {
  if (dismissTimer) window.clearTimeout(dismissTimer);
});
</script>

<style scoped>
.poola-opening {
  display: flex;
  justify-content: center;
  padding: 4px 16px 12px;
}

.poola-opening__banner {
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: var(--poolit-radius-full);
  background: var(--poolshare-rate-gold-bg);
  color: var(--poolshare-rate-gold-fg);
  font-weight: var(--poolit-font-weight-bold);
  text-align: center;
  /* Composite-only entrance: translate + opacity. */
  animation: poola-opening-in 0.5s cubic-bezier(0.2, 0.7, 0.3, 1);
}

.poola-opening__text {
  font-size: var(--poolit-font-size-body);
  line-height: var(--poolit-line-height-body);
}

@keyframes poola-opening-in {
  from {
    transform: translateY(-10px) scale(0.96);
    opacity: 0;
  }

  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* reduced-motion → a plain opacity fade, no translate/scale, no confetti (JS). */
.poola-opening[data-reduced='true'] .poola-opening__banner {
  animation: poola-opening-fade 0.4s ease;
}

@keyframes poola-opening-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .poola-opening__banner {
    animation: poola-opening-fade 0.4s ease;
  }
}
</style>
